import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const KIND = {
  gsc: {
    title: "Search Console property",
    overline: "Google Search Console",
    listUrl: "/google/search-console/sites",
    attachUrl: "/google/search-console/attach",
    failMsg: "Could not load Search Console properties. Please try again.",
  },
  ga4: {
    title: "GA4 property",
    overline: "Google Analytics (GA4)",
    listUrl: "/google/ga4/accounts",
    attachUrl: "/google/ga4/attach",
    failMsg: "Could not load GA4 properties. Please try again.",
  },
};

function sanitizedError(err, fallback) {
  const status = err?.response?.status;
  if (status === 401) return "Your session expired. Please sign in again.";
  if (status === 403) return "You don't have access to this project.";
  if (status === 400) return "Google is not connected for this project. Reconnect Google and try again.";
  return fallback;
}

// Flatten GSC sites / GA4 accounts into a uniform option list.
function toOptions(kind, data) {
  if (kind === "gsc") {
    return (data?.sites || []).map((s) => ({
      value: s.site_url,
      primary: s.site_url,
      secondary: s.permission_level,
    }));
  }
  const out = [];
  for (const acc of data?.accounts || []) {
    for (const p of acc.properties || []) {
      out.push({
        value: p.property_id,
        primary: p.display_name || p.property_id,
        secondary: `${acc.display_name || acc.account_id} · ${p.property_id}`,
      });
    }
  }
  return out;
}

export const GooglePropertyPicker = ({ kind, projectId, onClose, onAttached }) => {
  const cfg = KIND[kind];
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState("");
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(cfg.listUrl, { params: { project_id: projectId } });
        if (alive) setOptions(toOptions(kind, data));
      } catch (err) {
        if (alive) setError(sanitizedError(err, cfg.failMsg));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [kind, projectId, cfg.listUrl, cfg.failMsg]);

  const attach = async () => {
    if (!selected) return;
    setAttaching(true);
    try {
      const body = kind === "gsc"
        ? { project_id: projectId, site_url: selected }
        : { project_id: projectId, property_id: selected };
      await api.post(cfg.attachUrl, body);
      toast.success(`${cfg.overline} property attached`);
      onAttached();
    } catch (err) {
      toast.error(sanitizedError(err, `Could not attach the selected ${cfg.title}. Please try again.`));
    } finally {
      setAttaching(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40" data-testid={`google-picker-modal-${kind}`}>
      <div className="bg-white rounded-sm border border-border p-6 max-w-lg w-full mx-4">
        <p className="editorial-overline">{cfg.overline}</p>
        <h3 className="font-serif text-2xl mt-2">Select a {cfg.title}</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Only properties accessible to your connected Google account are listed. Read-only access.
        </p>

        <div className="mt-4 max-h-72 overflow-y-auto border border-border rounded-sm divide-y divide-border">
          {loading && (
            <p className="p-4 text-sm text-muted-foreground" data-testid={`google-picker-loading-${kind}`}>Loading properties…</p>
          )}
          {!loading && error && (
            <p className="p-4 text-sm text-red-700" data-testid={`google-picker-error-${kind}`}>{error}</p>
          )}
          {!loading && !error && options.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground" data-testid={`google-picker-empty-${kind}`}>
              No accessible properties found for this Google account.
            </p>
          )}
          {!loading && !error && options.map((o) => (
            <label
              key={o.value}
              className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-ivory-50 ${selected === o.value ? "bg-gold-500/10" : ""}`}
              data-testid={`google-picker-option-${kind}`}
            >
              <input
                type="radio"
                name={`google-${kind}-property`}
                value={o.value}
                checked={selected === o.value}
                onChange={() => setSelected(o.value)}
                className="mt-1 accent-[#c9a227]"
              />
              <span className="min-w-0">
                <span className="block text-sm break-all">{o.primary}</span>
                <span className="block text-xs text-muted-foreground font-mono break-all">{o.secondary}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 border border-border rounded-sm text-sm" data-testid={`google-picker-cancel-${kind}`}>
            Cancel
          </button>
          <button
            onClick={attach}
            disabled={!selected || attaching || loading}
            className="px-4 py-2 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm disabled:opacity-50"
            data-testid={`google-picker-attach-${kind}`}
          >
            {attaching ? "Attaching…" : "Attach property"}
          </button>
        </div>
      </div>
    </div>
  );
};
